<template>
  <div class="share-with-team">
    <h2 class="title is-3">Share "{{ deck.name }}" with team</h2>
    <p class="is-7">Show your slides in the team library and allow team members to host, collaborate, and copy</p>

    <p class="label" style="margin-bottom: 8px">Collaboration</p>
    <b-switch v-model="localTeamData.allowCopy" @input="updateAllowCopy" rounded>
      Allow members to copy
      <b-tooltip type="is-dark" label="Turn this on to let team members create a new copy in their own accounts that they can modify and use how they wish" position="is-top" multilined>
        <i class="fas fa-question-circle" />
      </b-tooltip>
    </b-switch>
    <b-switch v-model="localTeamData.allowEdit" rounded>Allow team to edit</b-switch>
    <!-- <b-switch v-model="localTeamData.isVisible" rounded>
      Show in team library
      <b-tooltip type="is-dark" label="Turn this off to stop your deck from showing in the team library while still allowing sharing via direct link" position="is-top" multilined>
        <i class="fas fa-question-circle" />
      </b-tooltip>
    </b-switch> -->
    <b-switch v-model="localTeamData.lockDeckSettings" rounded>
      Lock design and settings
      <b-tooltip type="is-dark" label="Prevent changes to the font, colors, and settings of the overall deck. Slides can still be edited unless they are locked individually" position="is-top" multilined>
        <i class="fas fa-question-circle" />
      </b-tooltip>
    </b-switch>

    <b-field label="Visibility" class="m-t-20">
      <b-select v-model="visibility" placeholder="Select a name">
        <option value="all">All team members</option>
        <option value="specific">Specific team members</option>
        <option value="none">Nobody (unlisted)</option>
      </b-select>
    </b-field>

    <div v-if="showSpecificEmails" class="m-b-20">
      <b-taginput
        v-model="localTeamData.visibleToUsers"
        ellipsis
        icon="user"
        icon-pack="fas"
        placeholder="Add user email"
        aria-close-label="Remove user"
      />
    </div>

    <b-switch v-model="publicVisibility" rounded>
      Allow public sharing
      <b-tooltip type="is-dark" label="Creates a public preview page and allows anyone to create a copy of this deck" position="is-top" multilined>
        <i class="fas fa-question-circle" />
      </b-tooltip>
    </b-switch>

    <div class="buttons m-t-40 is-centered">
      <b-button v-if="!deck.teamId" @click="duplicateAndShare()" size="is-medium" type="is-primary">Share deck with team</b-button>
      <b-button v-else @click="updateSharingSettings()" size="is-medium" type="is-primary">Save team sharing settings</b-button>
    </div>
  </div>
</template>

<script>
import { addDeckToTeam, updateTeamData } from '~/queries/teamControls.gql'
import { setPublicOrPrivate } from '~/queries/deckQueries.gql'

export default {
  props: ['deck'],
  data () {
    return {
      localTeamData: {
        teamId: undefined,
        allowEdit: true,
        allowCopy: true,
        isVisible: true,
        visibleToUsers: [],
        lockDeckSettings: false,
        originalVersion: undefined,
        isArchived: false
      },
      localPublicity: false,
      showSpecificEmails: false
    }
  },
  computed: {
    publicVisibility: {
      get () {
        return this.localPublicity
      },
      set (val) {
        this.localPublicity = val
        if (val === true) {
          this.localTeamData.allowCopy = true
          this.updateTeamData(this.deck.id, this.localTeamData)
        }
        this.setPublicOrPrivate(val)
      }
    },
    deckIndexComponent () {
      return this.findParentByName('DeckIndex')
    },
    parentComponent () {
      return this.$parent.$parent // first parent is modal wrapper, second is the launching page
    },
    isOwner () {
      return this.deck.userId === this.$auth.user.id
    },
    visibility: {
      get () {
        if (this.localTeamData.isVisible) {
          return 'all'
        } else if (this.showSpecificEmails) {
          return 'specific'
        } else {
          return 'none'
        }
      },
      set (value) {
        if (value === 'all') {
          this.localTeamData.isVisible = true
          this.showSpecificEmails = false
        } else if (value === 'none') {
          this.localTeamData.isVisible = false
          this.localTeamData.visibleToUsers = []
          this.showSpecificEmails = false
        } else {
          this.localTeamData.isVisible = false
          this.showSpecificEmails = true
        }
      }
    }
  },
  created () {
    this.localPublicity = this.deck.isPublic

    if (!this.isOwner) {
      this.$buefy.toast.open({ type: 'is-danger', message: 'Only deck owner can edit settings' })
      this.$emit('close')
    }
    if (this.deck.teamData) {
      this.mergeRemoteTeamData()
    }
    if (this.localTeamData.visibleToUsers.length > 0) {
      this.showSpecificEmails = true
    }
  },
  methods: {
    updateAllowCopy (val) {
      if (val === false) {
        this.publicVisibility = false
        this.updateTeamData(this.deck.id, this.localTeamData)
      }
    },
    updateVisibility (value) {
      if (value === 'all') {
        this.localTeamData.isVisible = true
      } else {
        this.localTeamData.isVisible = false
      }
      this.localTeamData.isVisible = value !== 'none'
    },
    mergeRemoteTeamData () {
      const remoteTeamData = { ...this.deck.teamData }
      for (const key in this.localTeamData) {
        if (remoteTeamData.hasOwnProperty(key)) {
          this.localTeamData[key] = remoteTeamData[key]
        }
      }
    },
    async setPublicOrPrivate (isPublic) {
      if (!this.isOwner) { return }
      this.log('setPublicOrPrivate', isPublic)
      await this.$apollo.mutate({
        mutation: setPublicOrPrivate,
        variables: {
          id: this.deck.id,
          isPublic
        }
      })
      this.deckIndexComponent.refreshDeckQueries()
    },
    async duplicateAndShare () {
      const teamId = this.$store.state.user?.team?.id
      if (!teamId) { this.throwError('No team Id'); return }

      const options = {
        refreshQueries: false,
        keepName: true
      }

      const newId = await this.parentComponent.duplicateDeck(this.deck.id, options)
      if (!newId) { this.log('no new deck id'); return }
      this.shareWithTeam(newId)
    },
    async shareWithTeam (newDeckId) {
      const teamId = this.$store.state.user?.team?.id
      if (!teamId) { this.throwError('No team Id'); return }
      this.parentComponent.changingDeck = true
      this.log(`sharing deck ${this.deck.id} with team ${teamId}`)

      const originalVersion = this.localTeamData.originalVersion || this.deck.id

      const teamData = this.localTeamData
      teamData.originalVersion = originalVersion
      teamData.teamId = teamId

      await this.$apollo.mutate({
        mutation: addDeckToTeam,
        variables: {
          id: newDeckId,
          teamId,
          teamData
        }
      })

      // update the original deck to add teamData and archive it
      await this.updateTeamData(this.deck.id, { teamId, isArchived: true, originalVersion })

      this.$buefy.toast.open({ type: 'is-success', message: 'Slide deck shared with team' })
      this.$emit('close')
    },
    async updateTeamData (deckId, teamData) {
      await this.$apollo.mutate({
        mutation: updateTeamData,
        variables: {
          id: deckId,
          teamData
        }
      })
      await this.deckIndexComponent.refreshDeckQueries()
    },
    async updateSharingSettings () {
      await this.updateTeamData(this.deck.id, this.localTeamData)
      this.$buefy.toast.open({ type: 'is-success', message: 'Sharing settings updated' })
      this.$emit('close')
    }
  }
}
</script>
<style lang="scss" scoped>
.pretty-scroll {
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    width: 5px;
    margin:8px 10px 8px 0px;
  }

  &::-webkit-scrollbar-thumb {
    width: 5px;
    border-radius:5px;
    background-color: #ccc;
  }
}

.share-with-team {
  background-color: white;
  padding: 2em;
  border-radius: 12px;  max-width: 500px;
  margin: 0 auto;

  p {
    margin-top: -.75em;
    margin-bottom: 1.25em;
  }
}
</style>
